import "@blocknote/core/fonts/inter.css";
import "@blocknote/shadcn/style.css";

import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/shadcn";
import { useMemo, useCallback, useEffect, useRef } from "react";
import { splitSoftBreaks } from "./markdownSoftBreaks";
import { parseConceptContent } from "./richTextUtils";

interface RichTextEditorProps {
  initialContent: string | null | undefined;
  onContentChange: (value: string) => void;
  uploadFile?: (file: File) => Promise<string>;
  className?: string;
  /**
   * "blocknote" (default) loads and emits BlockNote JSON (idea bodies).
   * "markdown" loads and emits markdown text (notes, TD-092). Content is read
   * once on mount, so remount with a key to load a different note.
   */
  format?: "blocknote" | "markdown";
  /** Shown while the document is empty. */
  placeholder?: string;
  /** Focus the end of the content on mount. */
  autoFocus?: boolean;
}

export function RichTextEditor({
  initialContent,
  onContentChange,
  uploadFile,
  className,
  format = "blocknote",
  placeholder,
  autoFocus = false,
}: RichTextEditorProps) {
  const isMarkdown = format === "markdown";
  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;
  const initialMarkdownRef = useRef(initialContent ?? "");

  const blocks = useMemo(
    () => (isMarkdown ? undefined : parseConceptContent(initialContent)),
    [initialContent, isMarkdown]
  );

  const handleUpload = useCallback(
    async (file: File) => {
      if (!uploadFile) throw new Error("File upload not available");
      return uploadFile(file);
    },
    [uploadFile]
  );

  const editor = useCreateBlockNote({
    initialContent: blocks,
    ...(uploadFile && { uploadFile: handleUpload }),
    ...(autoFocus && { autofocus: "end" }),
    // Deprecated in favour of `dictionary`, but that route needs the
    // `@blocknote/core/locales` subpath, which `moduleResolution: node` can't type.
    ...(placeholder && { placeholders: { emptyDocument: placeholder } }),
  });

  useEffect(() => {
    if (!isMarkdown || !initialMarkdownRef.current.trim()) return;
    const parsed = editor.tryParseMarkdownToBlocks(splitSoftBreaks(initialMarkdownRef.current));
    if (parsed.length > 0) editor.replaceBlocks(editor.document, parsed);
  }, [editor, isMarkdown]);

  const handleChange = useCallback(() => {
    const value = isMarkdown
      ? editor.blocksToMarkdownLossy(editor.document)
      : JSON.stringify(editor.document);
    onContentChangeRef.current(value);
  }, [editor, isMarkdown]);

  return (
    <div className={className} data-rich-text-editor>
      <BlockNoteView
        editor={editor}
        onChange={handleChange}
        theme="light"
      />
    </div>
  );
}
